// src/features/services/sales/components/CreateWorkOrderPanel.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Create a new Production / Work Order.
// Selects a finished-product SKU, auto-resolves its BOM, and shows
// the exploded component requirements before creation.
// PAC Glass surfaces. Data owned by useProductionOrders + useBom hooks.
// ─────────────────────────────────────────────────────────────────────────────
import React, { useState, useMemo, useCallback } from 'react';
import { useProductionOrders } from '../hooks/useProductionOrders';
import { useBom } from '../hooks/useBom';
import { useSkuCatalog } from '../hooks/useSkuCatalog';
import type { BomDefinition } from '../api/bomApi';

export default function CreateWorkOrderPanel() {
  const { create } = useProductionOrders();
  const bomQuery = useBom({ active: true });
  const skuQuery = useSkuCatalog();

  const boms: BomDefinition[] = bomQuery.data?.items ?? [];
  const allSkus = skuQuery.data?.items ?? [];

  // Only show SKUs that have an active BOM
  const skusWithBom = useMemo(() => {
    const bomFinishedSkus = new Set(boms.map(b => b.finished_sku));
    return allSkus.filter(s => bomFinishedSkus.has(s.sku) && s.active);
  }, [boms, allSkus]);

  const [showForm, setShowForm] = useState(false);
  const [finishedSku, setFinishedSku] = useState('');
  const [quantity, setQuantity] = useState(1);
  const [label, setLabel] = useState('');
  const [notes, setNotes] = useState('');

  // Resolve the BOM for selected SKU
  const selectedBom = useMemo(
    () => boms.find(b => b.finished_sku === finishedSku) ?? null,
    [boms, finishedSku],
  );

  // Exploded component requirements
  const explodedComponents = useMemo(() => {
    if (!selectedBom) return [];
    return selectedBom.components.map(c => ({
      ...c,
      required_qty: c.quantity * quantity,
    }));
  }, [selectedBom, quantity]);

  const resetForm = useCallback(() => {
    setFinishedSku('');
    setQuantity(1);
    setLabel('');
    setNotes('');
    setShowForm(false);
  }, []);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!finishedSku || !selectedBom) return;

    create.mutate(
      {
        finished_sku: finishedSku,
        quantity,
        label: label || undefined,
        notes: notes || undefined,
      },
      { onSuccess: () => resetForm() },
    );
  };

  return (
    <div className="pac-glass rounded-lg p-5 space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold text-neutral-200 uppercase tracking-wider">
          Work Orders
        </h2>
        <button
          onClick={() => setShowForm(f => !f)}
          className="pac-glass-btn text-xs px-3 py-1.5 rounded border border-white/10 text-neutral-300
                     hover:border-emerald-500/50 hover:text-emerald-400 transition-colors"
        >
          {showForm ? 'Cancel' : '+ New Work Order'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="pac-glass-raised rounded p-4 space-y-4 border border-neutral-800">
          {/* Finished SKU */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Product to Manufacture
            </label>
            <select
              value={finishedSku}
              onChange={e => setFinishedSku(e.target.value)}
              className="pac-glass-input w-full rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         focus:ring-1 focus:ring-emerald-500/30 outline-none"
              required
            >
              <option value="">Select product…</option>
              {skusWithBom.map(s => (
                <option key={s.sku} value={s.sku}>{s.label} ({s.sku})</option>
              ))}
            </select>
          </div>

          {/* Quantity */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Quantity
            </label>
            <input
              type="number"
              value={quantity}
              onChange={e => setQuantity(Math.max(1, Number(e.target.value)))}
              min={1}
              max={10000}
              className="pac-glass-input w-32 rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         focus:ring-1 focus:ring-emerald-500/30 outline-none text-center"
              required
            />
          </div>

          {/* Label */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Label (optional)
            </label>
            <input
              type="text"
              value={label}
              onChange={e => setLabel(e.target.value)}
              placeholder="e.g. Batch March 2026"
              className="pac-glass-input w-full rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         focus:ring-1 focus:ring-emerald-500/30 outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="block text-[10px] text-neutral-500 uppercase tracking-wider mb-1">
              Notes (optional)
            </label>
            <textarea
              value={notes}
              onChange={e => setNotes(e.target.value)}
              rows={2}
              className="pac-glass-input w-full rounded px-3 py-2 text-sm text-neutral-200
                         bg-white/[0.04] border border-white/10 focus:border-emerald-500/50
                         focus:ring-1 focus:ring-emerald-500/30 outline-none resize-none"
            />
          </div>

          {/* ── BOM Preview (exploded components) ─────────────────────── */}
          {selectedBom && explodedComponents.length > 0 && (
            <div className="pac-glass-subtle rounded p-3 space-y-2 border border-white/5">
              <p className="text-[10px] text-neutral-500 uppercase tracking-wider">
                BOM: {selectedBom.label} — Component Requirements
              </p>
              <div className="space-y-1">
                {explodedComponents.map((c, i) => (
                  <div key={i} className="flex items-center gap-3 text-xs text-neutral-300">
                    <span className="text-emerald-400 w-8 text-right font-mono">
                      {c.required_qty}×
                    </span>
                    <span className="font-mono">{c.sku}</span>
                    <span className="text-neutral-500">({c.unit})</span>
                    <span className="text-neutral-600 text-[10px]">
                      ({c.quantity}× per unit × {quantity})
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {finishedSku && !selectedBom && (
            <p className="text-xs text-amber-400">No active BOM found for this SKU.</p>
          )}

          {/* Submit */}
          <div className="flex gap-3 pt-2">
            <button
              type="submit"
              disabled={create.isPending || !selectedBom}
              className="pac-glass-btn px-4 py-2 rounded text-sm border border-emerald-500/40
                         text-emerald-400 hover:border-emerald-400 transition-colors
                         disabled:opacity-40 disabled:cursor-not-allowed"
            >
              {create.isPending ? 'Creating…' : 'Create Work Order'}
            </button>
          </div>

          {create.isError && (
            <p className="text-xs text-rose-400">{create.error.message}</p>
          )}
          {create.isSuccess && (
            <div className="pac-glass-subtle rounded p-3 border border-emerald-900/40">
              <p className="text-xs text-emerald-400">
                Work order created — {create.data.serial_number}
              </p>
            </div>
          )}
        </form>
      )}
    </div>
  );
}
